/* eslint-disable max-lines */
import omit from 'lodash/omit'
import Qs from 'qs'
import { getRouteByLocation } from '../utils/route'
import { RouteModel, VisitedModel, LocationModel, OptionsModel, IgnoreVisitedKeysModel } from './interface'

const OMIT_LOCATION_KEYS = ['key', 'useCache']

const DELAY_REMOVE_TIME = 30

// 清除url中的一些参数
const omitLocation = (location: LocationModel) => {
    if (location.query) {
        delete location.query.token

        location.search = Qs.stringify(location.query, {
            addQueryPrefix: true
        })
    }

    return location
}

class Manager {
    // 缓存控制器
    aliveController: any = null
    // 路由
    history: any = null

    // 滚动容器
    scroller: any = null

    // 路由表
    routes: RouteModel[] = []

    // 访问记录
    visitedList: VisitedModel[] = []

    // 最近的
    lastVisitedName = ''

    ignoreVisitedKeys: IgnoreVisitedKeysModel

    // 额外信息
    infoMap: any = new Map()

    // 默认是否使用缓存
    defaultUseCache = false

    // 不销毁微应用
    unDropMicroApp = false

    // 额外路由匹配
    matchRoute: any = null

    refreshMicroApp: any = null

    // 缓存变更回调
    onVisitedChange: any = null

    constructor({
        history,
        aliveController,
        routes,
        scroller,
        defaultUseCache,
        unDropMicroApp,
        matchRoute,
        refreshMicroApp,
        ignoreVisitedKeys,
        onVisitedChange
    }: OptionsModel) {
        this.aliveController = aliveController
        this.history = history
        this.routes = routes
        this.scroller = scroller
        this.defaultUseCache = defaultUseCache
        this.unDropMicroApp = unDropMicroApp
        this.ignoreVisitedKeys = ignoreVisitedKeys

        this.matchRoute = matchRoute
        this.refreshMicroApp = refreshMicroApp
        this.onVisitedChange = onVisitedChange

        this.init()
    }

    // 初始化
    init() {
        this._listenHistory()
    }

    // 根据location在路由表中匹配
    _machRouteByLocation(location: Location) {
        const { routes } = this

        const matchedRoute = getRouteByLocation({
            location,
            routes
        })

        if (typeof this.matchRoute === 'function') {
            return this.matchRoute({
                location,
                matchedRoute
            })
        }

        return matchedRoute
    }

    // 获取滚动条高度
    _getScrollTop() {
        const { scroller } = this

        if (scroller) {
            return scroller.getScrollTop()
        }

        return 0
    }

    // 滚动到
    _scrollTo(scrollTop: number) {
        const { scroller } = this

        if (scroller) {
            scroller.scrollTo(scrollTop)
        }
    }

    // 保存信息
    _saveInfo(name: string, data: any) {
        const info = this.infoMap.get(name) || {}

        this.infoMap.set(name, {
            ...info,
            ...(data || {})
        })
    }

    // 获取信息
    _getInfo(name: string) {
        return this.infoMap.get(name)
    }

    _emitVisitedChange() {
        this.onVisitedChange && this.onVisitedChange(this.visitedList)
    }

    _shouldVisitedIgnore({ location, route }: { location: LocationModel; route: RouteModel }) {
        if (typeof this.ignoreVisitedKeys === 'function') {
            return this.ignoreVisitedKeys({ location, route })
        }

        if (Array.isArray(this.ignoreVisitedKeys)) {
            return this.ignoreVisitedKeys.includes(route.name)
        }

        return false
    }

    // 检测是否是微应用
    _checkIsMicroApp(name: string) {
        const { getCachingNodes } = this.aliveController

        const cacheNodes = getCachingNodes()

        const targetCache = cacheNodes.find((v: any) => v.name === name)

        if (targetCache) {
            return targetCache.microApp || targetCache.children?.props?.hasOwnProperty('microAppPath')
        }

        return false
    }

    // 移除记录
    removeVisited(visited: any) {
        const { drop } = this.aliveController

        const arr = Array.isArray(visited) ? visited : [visited]

        arr.forEach(v => {
            const isMicroApp = this._checkIsMicroApp(v.name)

            if (isMicroApp) {
                if (this.unDropMicroApp) {
                    this.refreshMicroApp && this.refreshMicroApp(v.name)
                } else {
                    drop(v.name)
                }
            } else {
                drop(v.name)
            }

            this._removeInfo(v.name)
        })

        setTimeout(() => {
            this.visitedList = this.visitedList.filter(v => !arr.some(c => c.name === v.name))

            this._emitVisitedChange()
        }, DELAY_REMOVE_TIME)
    }

    // 更新记录
    updateVisited(data: { name: string; [key: string]: any }) {
        if (!data) {
            return
        }

        this.visitedList = this.visitedList.map(v => {
            if (v.name === data.name) {
                return {
                    ...v,
                    ...data
                }
            }

            return v
        })

        this._emitVisitedChange()
    }

    // 设置记录
    shiftVisited(data: VisitedModel[]) {
        this.visitedList = [...data, ...this.visitedList]

        this._emitVisitedChange()
    }

    // 添加记录
    pushVisited(data: VisitedModel[]) {
        this.visitedList = [...data, ...this.visitedList]

        this._emitVisitedChange()
    }

    // 记录进入信息
    _recordEnterInfo(name: string) {
        this._saveInfo(name, {
            lastEnterTime: Date.now() // 进入时间
        })
    }

    // 移除信息
    _removeInfo(name: string) {
        this.infoMap.delete(name)
    }

    // 还原滚动位置
    restoreScrollPosition(name: string) {
        const info = this._getInfo(name)

        this._scrollTo(info?.scrollTop || 0)
    }

    // 记录离开信息
    _recordLeaveInfo(name: string) {
        this._saveInfo(name, {
            lastLeaveTime: Date.now(), // 离开时间
            scrollTop: this._getScrollTop() // 滚动高度
        })
    }

    // 记录访问
    _pushVisited({ route, location }: { route: RouteModel; location: LocationModel }) {
        const isExisted = this.visitedList.some((v: any) => v.name === route.name)

        if (isExisted) {
            this.visitedList = this.visitedList.map((v: any) => {
                if (v.name === route.name) {
                    return {
                        ...v,
                        location: omitLocation(location),
                        timeStamp: Date.now()
                    }
                }

                return v
            })
        } else {
            this.visitedList = [
                ...this.visitedList,
                {
                    name: route.name,
                    title: route.title || '',
                    path: route.path,
                    location: omitLocation(location),
                    timeStamp: Date.now()
                }
            ]
        }

        this.lastVisitedName = route.name

        this._emitVisitedChange()
    }

    // 记录所有变更的tab
    // 在这里还原滚动高有问题
    _listenHandler(nextLocation: any) {
        const matched: any = this._machRouteByLocation(nextLocation)

        if (!matched?.name) {
            return
        }

        const shouldIgnore = this._shouldVisitedIgnore({ location: nextLocation, route: matched })

        if (shouldIgnore) {
            return
        }

        this._pushVisited({
            route: matched,
            location: nextLocation
        })
        this._recordEnterInfo(matched.name)
    }

    // 路由监听
    _listenHistory() {
        if (!this.history || !this.aliveController) {
            return
        }

        this.history.listen(this._listenHandler.bind(this))
    }

    // 是否还原location
    _shouldRestoreLocation({ nextRoute, nextLocation }: { nextRoute: RouteModel; nextLocation: LocationModel }) {
        const { refreshScope } = this.aliveController

        const isMicroApp = this._checkIsMicroApp(nextRoute.name)

        // 没开启时
        if (!this.defaultUseCache) {
            // 通过useCache决定是否使用
            if (!nextLocation.useCache) {
                // 更新缓存

                this._removeInfo(nextRoute.name)

                if (!isMicroApp || !this.unDropMicroApp) {
                    refreshScope(nextRoute.name)
                }
                return true
            }
        }

        // 是否更新缓存
        if (nextLocation.refresh) {
            this._removeInfo(nextRoute.name)

            if (!isMicroApp || !this.unDropMicroApp) {
                refreshScope(nextRoute.name)
            }
            return true
        }

        const visited = this.visitedList.find((v: any) => v.name === nextRoute.name)

        // 存在缓存，则将缓存的location合并
        if (visited) {
            // 还原url
            Object.assign(nextLocation, omit(visited.location, OMIT_LOCATION_KEYS))
        }

        return true
    }

    _blockHandler(nextLocation: any, action: string) {
        // 后退
        if (action === 'POP') {
            return true
        }

        try {
            const curLocation = this.history.location

            const curRoute = this._machRouteByLocation(curLocation)
            const nextRoute = this._machRouteByLocation(nextLocation)

            if (curRoute && curRoute.name) {
                this._recordLeaveInfo(curRoute.name)
            }

            // 未匹配到
            if (!nextRoute || !curRoute) {
                return true
            }

            // 当前页
            if (curRoute && nextRoute && curRoute.name === nextRoute.name) {
                return true
            }

            return this._shouldRestoreLocation({ nextRoute, nextLocation })
        } catch (e) {
            console.error('catchError', e)
        }

        return true
    }

    deleteVisitedParams(name: string, paramKeys?: string[]) {
        this.visitedList.forEach((v: any) => {
            if (v.name === name) {
                if (paramKeys) {
                    paramKeys.forEach(pKey => {
                        delete v.location.query[pKey]
                    })
                } else {
                    Object.keys(v.location.query || {}).forEach(key => {
                        delete v.location.query[key]
                    })
                }

                v.location.search = Qs.stringify(v.location.query, {
                    addQueryPrefix: true
                })
            }
        })
    }

    // 路由拦截
    // url参数还原、如果携带refresh：true，不还原且清除缓存
    blockHistory(...args) {
        if (!this.history || !this.aliveController) {
            return
        }

        this._blockHandler(...args)
    }
}

export default Manager
